/**
 * Technology Footprint Widget (issue #1654, part of epic #1650)
 *
 * Shows technologies ranked by service count, matching
 * `homepage-dashboard-spec.md`'s "Technology Footprint" widget. Fixed to
 * the spec's documented `?limit=10` example, all categories (no per-instance
 * config UI yet — the same MVP scope every sibling epic-#1650 widget ships
 * at). Follows the single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import { useTechnologyFootprint, type TechnologyFootprintEntry } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './TechnologyFootprintWidget.css';

/** Matches the spec's documented `?limit=10` example. */
const FOOTPRINT_LIMIT = 10;

const WIDGET_CONFIG: WidgetConfig = {
  id: 'technology-footprint',
  title: 'Technology Footprint',
  size: 'large',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface TechnologyFootprintStats {
  technologies: TechnologyFootprintEntry[];
}

type TechnologyFootprintQueryData = ReturnType<typeof useTechnologyFootprint>['data'];

function extractTechnologyFootprintStats(
  data: TechnologyFootprintQueryData
): TechnologyFootprintStats | null {
  if (!data?.technologyFootprint) {
    return null;
  }
  return data.technologyFootprint;
}

export function TechnologyFootprintWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useTechnologyFootprint(undefined, FOOTPRINT_LIMIT),
    extractTechnologyFootprintStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.technologies.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="technology-footprint-widget__empty">
          <IconElement name="memory" className="technology-footprint-widget__empty-icon" />
          <p className="forge-typography--body2">No technology usage data available</p>
        </div>
      </DashboardWidget>
    );
  }

  const maxServiceCount = Math.max(...stats.technologies.map((t) => t.serviceCount), 1);

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="technology-footprint-widget__list" data-testid="technology-footprint-list">
        {stats.technologies.map((entry) => (
          <li
            key={entry.id}
            className="technology-footprint-widget__row"
            data-testid="technology-footprint-row"
          >
            <div className="technology-footprint-widget__row-header">
              <span className="technology-footprint-widget__name">{entry.name}</span>
              <span className="technology-footprint-widget__count">
                {entry.serviceCount} service{entry.serviceCount === 1 ? '' : 's'}
              </span>
            </div>
            <div
              className="technology-footprint-widget__bar-track"
              role="img"
              aria-label={`${entry.name}: ${entry.serviceCount} services`}
            >
              <div
                className="technology-footprint-widget__bar-fill"
                style={{ width: `${(entry.serviceCount / maxServiceCount) * 100}%` }}
              />
            </div>
            <div className="technology-footprint-widget__meta">
              <span className="technology-footprint-widget__category forge-typography--caption">
                {entry.category}
              </span>
              <span
                className={`technology-footprint-widget__status technology-footprint-widget__status--${entry.strategicStatus.toLowerCase()}`}
              >
                {entry.strategicStatus}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
